import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const FEATURE_COLORS = {
  matrix_reminder: '#6366f1',
  hold_digest: '#f59e0b',
  schedule_progress: '#3b82f6',
  schedule_export: '#10b981',
  over_allocation: '#ef4444',
  timesheet_submit: '#8b5cf6',
  mention: '#06b6d4',
  ticket_admin: '#ec4899',
  ticket_reply: '#f97316',
  welcome: '#14b8a6',
  generic: '#6b7280'
};

const STATUS_STYLES = {
  sent: { bg: '#d4edda', color: '#155724', label: 'Sent' },
  failed: { bg: '#f8d7da', color: '#721c24', label: 'Failed' },
  skipped: { bg: '#e2e3e5', color: '#41464b', label: 'Skipped' }
};

const EmailLogPage = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('emailLog', 'global', true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [featureFilter, setFeatureFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [features, setFeatures] = useState({});
  const LIMIT = 25;

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      if (featureFilter) params.append('feature', featureFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const response = await authenticatedApiCall(instance, accounts, 'get', `/notification-preferences/email-log?${params}`);
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch email log:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, page, featureFilter, statusFilter, search]);

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'get', '/notification-preferences/features');
      setFeatures(response.data.features || {});
    } catch (err) {
      console.error('Failed to fetch features:', err);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  // Debounce the search box so we don't query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const FeatureBadge = ({ feature }) => {
    const color = FEATURE_COLORS[feature] || '#6b7280';
    const label = features[feature]?.label || feature;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
        fontSize: '11px', fontWeight: 600, color: 'white', backgroundColor: color,
        whiteSpace: 'nowrap'
      }}>{label}</span>
    );
  };

  const StatusBadge = ({ status }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.sent;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 600, color: style.color, backgroundColor: style.bg
      }}>{style.label}</span>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0, fontSize: '22px', color: '#2c3e50' }}>
            <span style={{ fontSize: '28px' }}>📧</span>
            Email Notification Log
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: '14px' }}>
            Live log of all automated emails sent by the system &middot; {total} total entries
          </p>
        </div>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: '220px' }}>
          <span style={{
            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
            color: '#adb5bd', fontSize: '14px', pointerEvents: 'none'
          }}>🔍</span>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by recipient, email, subject…"
            style={{
              width: '100%', padding: '8px 30px 8px 32px', borderRadius: '6px',
              border: '1px solid #ced4da', fontSize: '13px', boxSizing: 'border-box'
            }}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              title="Clear search"
              style={{
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                border: 'none', background: 'none', cursor: 'pointer', color: '#adb5bd',
                fontSize: '16px', lineHeight: 1, padding: 0
              }}
            >&times;</button>
          )}
        </div>

        <select
          value={featureFilter}
          onChange={(e) => { setFeatureFilter(e.target.value); setPage(0); }}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ced4da',
            fontSize: '13px', backgroundColor: 'white', minWidth: '200px'
          }}
        >
          <option value="">All Features</option>
          {Object.entries(features).map(([key, meta]) => (
            <option key={key} value={key}>{meta.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ced4da',
            fontSize: '13px', backgroundColor: 'white'
          }}
        >
          <option value="">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>

        <button
          onClick={() => fetchLog()}
          style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #ced4da',
            fontSize: '13px', backgroundColor: '#f8f9fa', cursor: 'pointer'
          }}
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef',
        overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>Loading...</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>📭</div>
            <p>No email log entries found{featureFilter || statusFilter || search ? ' for the selected filters' : ' yet'}.</p>
            <p style={{ fontSize: '13px' }}>Emails will be logged here once sent.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Feature</th>
                <th style={thStyle}>Subject</th>
                <th style={thStyle}>Recipients</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Env</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={tdStyle}>
                    <span style={{ whiteSpace: 'nowrap', color: '#495057' }}>{formatDate(entry.created_at)}</span>
                  </td>
                  <td style={tdStyle}><FeatureBadge feature={entry.feature} /></td>
                  <td style={{ ...tdStyle, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.subject}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ maxWidth: '250px' }}>
                      {(entry.recipients_to || []).map((email, i) => (
                        <span key={i} style={{
                          display: 'inline-block', padding: '1px 6px', margin: '1px 2px',
                          backgroundColor: '#e9ecef', borderRadius: '3px', fontSize: '11px'
                        }}>{email}</span>
                      ))}
                    </div>
                  </td>
                  <td style={tdStyle}><StatusBadge status={entry.status} /></td>
                  <td style={tdStyle}>
                    <span style={{
                      fontSize: '11px', padding: '2px 6px', borderRadius: '3px',
                      backgroundColor: entry.environment === 'production' ? '#cce5ff' : '#fff3cd',
                      color: entry.environment === 'production' ? '#004085' : '#856404'
                    }}>
                      {entry.environment === 'production' ? 'PROD' : 'DEV'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
          marginTop: '16px'
        }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={paginationBtnStyle(page === 0)}
          >Previous</button>
          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={paginationBtnStyle(page >= totalPages - 1)}
          >Next</button>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px',
  fontWeight: 600, color: '#495057', textTransform: 'uppercase',
  letterSpacing: '0.3px'
};

const tdStyle = {
  padding: '10px 12px', verticalAlign: 'middle'
};

const paginationBtnStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: '4px', fontSize: '13px',
  border: '1px solid #ced4da', cursor: disabled ? 'default' : 'pointer',
  backgroundColor: disabled ? '#e9ecef' : 'white',
  color: disabled ? '#adb5bd' : '#495057'
});

export default EmailLogPage;
